import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { formatDateUK } from '../../lib/utils';
import { Eye, Lock, Plus, Edit2, Trash2, NotebookPen, Tag } from 'lucide-react';

const DEFAULT_CATEGORIES = ['Shape', 'Defence', 'Attack', 'Set Pieces', 'Formation'];
const NONE_CATEGORY = '__none__';

const generateId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

const blankForm = { body: '', category: '', visibility: 'private' };

export const CoachNotesTab = ({
  notes = [],
  readOnly = false,
  categories = DEFAULT_CATEGORIES,
  onChange
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [filter, setFilter] = useState('all');

  const visibleNotes = readOnly ? notes.filter(n => (n.visibility || 'private') === 'public') : notes;

  const filtered = useMemo(() => {
    if (filter === 'all') return visibleNotes;
    if (filter === '__uncategorised__') return visibleNotes.filter(n => !n.category);
    return visibleNotes.filter(n => n.category === filter);
  }, [visibleNotes, filter]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...blankForm });
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setEditingId(note.id);
    setForm({
      body: note.body || '',
      category: note.category || '',
      visibility: note.visibility || 'private'
    });
    setDialogOpen(true);
  };

  const saveNote = () => {
    if (!form.body.trim()) return;
    const nowIso = new Date().toISOString();
    const payload = {
      body: form.body.trim(),
      category: form.category || null,
      visibility: form.visibility || 'private',
    };
    let nextNotes;
    if (editingId) {
      nextNotes = notes.map(n => n.id === editingId ? { ...n, ...payload, updated_at: nowIso } : n);
    } else {
      nextNotes = [...notes, { id: generateId(), ...payload, created_at: nowIso, updated_at: null }];
    }
    onChange?.({ coach_notes: nextNotes });
    setDialogOpen(false);
  };

  const deleteNote = (note) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    onChange?.({ coach_notes: notes.filter(n => n.id !== note.id) });
  };

  const categoryOptions = [...new Set([...(categories || []), ...notes.map(n => n.category).filter(Boolean)])];

  return (
    <Card data-testid="coach-notes-tab">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-xl font-semibold font-title">Coach's Notes</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {readOnly
                ? "Public notes shared by your coach for this match."
                : 'Post multiple notes per match. Choose a category and set each one private or public.'}
            </p>
          </div>
          {!readOnly && (
            <Button onClick={openAdd} className="btn-primary rounded-xl" data-testid="coach-note-add-btn">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Note
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Category filter */}
        {visibleNotes.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-gray-500 shrink-0">Filter by category</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-52 rounded-xl" data-testid="coach-notes-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                <SelectItem value="__uncategorised__">Uncategorised</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-gray-200 rounded-2xl">
            <NotebookPen className="w-10 h-10 mx-auto mb-2 text-gray-300" strokeWidth={1.5} />
            <p className="text-sm text-gray-500">
              {readOnly
                ? 'Your coach has not shared any notes for this match yet.'
                : visibleNotes.length === 0
                ? 'No notes yet — add your first one above.'
                : 'No notes in this category.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="coach-notes-list">
            {filtered.map(note => <NoteCard key={note.id} note={note} readOnly={readOnly} onEdit={openEdit} onDelete={deleteNote} />)}
          </div>
        )}
      </CardContent>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="coach-note-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Note' : 'Add Note'}</DialogTitle>
            <DialogDescription>Write a match note, pick a category, and set its visibility.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category || NONE_CATEGORY}
                  onValueChange={(v) => setForm({ ...form, category: v === NONE_CATEGORY ? '' : v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="coach-note-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_CATEGORY}>— None —</SelectItem>
                    {categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visibility</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="coach-note-visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">
                      <span className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gray-500" strokeWidth={1.5} />Private</span>
                    </SelectItem>
                    <SelectItem value="public">
                      <span className="flex items-center gap-2"><Eye className="w-3.5 h-3.5 text-emerald-600" strokeWidth={1.5} />Public</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                rows={8}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="e.g. Press high on goal kicks, keep shape for the first 15 minutes, watch their #7 on counters…"
                className="rounded-xl"
                data-testid="coach-note-body-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="coach-note-cancel-btn">Cancel</Button>
              <Button onClick={saveNote} disabled={!form.body.trim()} className="btn-primary" data-testid="coach-note-save-btn">
                {editingId ? 'Save Changes' : 'Add Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

function NoteCard({ note, readOnly, onEdit, onDelete }) {
  const isPublic = (note.visibility || 'private') === 'public';
  return (
    <article
      className="p-4 rounded-2xl bg-gray-50 border border-gray-200"
      data-testid={`coach-note-${note.id}`}
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {note.category && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
              <Tag className="w-3 h-3" strokeWidth={2} />{note.category}
            </span>
          )}
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isPublic ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'}`}>
            {isPublic ? <Eye className="w-3 h-3" strokeWidth={2} /> : <Lock className="w-3 h-3" strokeWidth={2} />}
            {isPublic ? 'Public' : 'Private'}
          </span>
          {note.created_at && (
            <span className="text-xs text-gray-400 truncate">{formatDateUK(note.created_at.slice(0, 10))}</span>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => onEdit(note)} className="text-gray-500 hover:text-gray-700 rounded-lg" data-testid={`coach-note-edit-btn-${note.id}`} title="Edit">
              <Edit2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(note)} className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`coach-note-delete-btn-${note.id}`} title="Delete">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        )}
      </header>
      <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{note.body}</p>
    </article>
  );
}

export default CoachNotesTab;
